import { useEffect, useRef, useState } from 'react';
import { useI18nText } from '@/lib/utils/i18nText';
import { getSearchHistory, removeSearchHistoryItem } from '@/lib/utils/searchHistory';

// Delay before a keystroke is pushed up to the parent filter state. Typing updates only
// the local draft (cheap); the parent commit — which re-renders the whole dashboard table —
// runs once typing pauses instead of on every character.
const COMMIT_DEBOUNCE_MS = 200;

/**
 * Sidebar search input with a custom recent-search dropdown.
 * Unlike a native <datalist>, each suggestion has its own delete (×) button so the
 * user can prune individual keywords. History is read from / written to localStorage
 * (see searchHistory.ts); the parent records new entries on Apply/Enter.
 *
 * The typed text is held in a local `draft` so keystrokes re-render only this small input.
 * The value is pushed to the parent (`onChange`) on a short debounce — and immediately on
 * Enter, blur, clear, or suggestion pick — so the expensive table render happens at most
 * once per typing pause instead of once per character.
 */
export function RecentSearchInput({
  value,
  onChange,
  onKeyDown,
  placeholder,
  historyKey,
}: {
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  placeholder?: string;
  /** localStorage history key (e.g. 'channelName'). */
  historyKey: string;
}) {
  const { t } = useI18nText();
  const [open, setOpen] = useState(false);
  // Keyboard-highlighted suggestion (-1 = none); driven by Arrow Up/Down.
  const [activeIndex, setActiveIndex] = useState(-1);
  const [, bump] = useState(0); // force re-read after an item is removed
  const wrapRef = useRef<HTMLDivElement>(null);

  // Local draft of the typed text; the parent `value` is only the committed (debounced) value.
  const [draft, setDraft] = useState(value);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  // Tracks the last value we pushed up, to tell our own echo (parent re-render with our value)
  // apart from a genuine external change (Clear, reset, URL nav, suggestion set by parent).
  const lastCommittedRef = useRef(value);

  const commit = (v: string) => {
    clearTimeout(timerRef.current);
    if (v === lastCommittedRef.current) return;
    lastCommittedRef.current = v;
    onChangeRef.current(v);
  };
  const scheduleCommit = (v: string) => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => commit(v), COMMIT_DEBOUNCE_MS);
  };

  // Adopt external changes to `value` (Clear/reset/URL); ignore the echo of our own commit.
  useEffect(() => {
    if (value !== lastCommittedRef.current) {
      lastCommittedRef.current = value;
      setDraft(value);
    }
  }, [value]);
  useEffect(() => () => clearTimeout(timerRef.current), []);
  // Reset the keyboard highlight whenever the dropdown closes (click-outside, etc.).
  useEffect(() => { if (!open) setActiveIndex(-1); }, [open]);

  const hasValue = !!draft;
  const all = getSearchHistory(historyKey);
  const q = draft.trim().toLowerCase();
  const suggestions = q ? all.filter((s) => s.toLowerCase().includes(q)) : all;
  const dropdownVisible = open && suggestions.length > 0;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const removeItem = (item: string) => {
    removeSearchHistoryItem(historyKey, item);
    bump((v) => v + 1);
  };

  return (
    <div className="filter-section--compact recent-search" ref={wrapRef}>
      <div className={`filter-input-wrap${hasValue ? ' filter-input-wrap--has-value' : ''}`}>
        <input
          type="text"
          className="filter-input"
          value={draft}
          onChange={(e) => { setDraft(e.target.value); scheduleCommit(e.target.value); setActiveIndex(-1); setOpen(true); }}
          onKeyDown={(e) => {
            // Consume Arrow keys for the dropdown (stop them from also bubbling to
            // some ancestor's own Arrow-key navigation — e.g. QuickSearchDialog's
            // result list) whenever there's a suggestion to navigate, regardless of
            // whether `open` is already true — the very first ArrowDown both opens
            // the dropdown AND highlights row 0 in the same keystroke, and `open`
            // itself won't reflect that until next render, so checking `open` alone
            // here would let this first press slip through to the ancestor too.
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              if (suggestions.length > 0) e.stopPropagation();
              if (!open) { setOpen(true); setActiveIndex(suggestions.length > 0 ? 0 : -1); return; }
              if (suggestions.length > 0) setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
              return;
            }
            if (e.key === 'ArrowUp') {
              e.preventDefault();
              if (suggestions.length > 0) e.stopPropagation();
              if (dropdownVisible) setActiveIndex((i) => Math.max(i - 1, 0));
              return;
            }
            if (e.key === 'Enter') {
              // Pick the keyboard-highlighted history item (same as clicking it).
              if (open && activeIndex >= 0 && suggestions[activeIndex]) {
                e.preventDefault();
                e.stopPropagation();
                const picked = suggestions[activeIndex];
                setDraft(picked);
                commit(picked);
                setOpen(false);
                setActiveIndex(-1);
                return;
              }
              setOpen(false);
              setActiveIndex(-1);
              commit(draft);
              onKeyDown?.(e);
              return;
            }
            if (e.key === 'Escape') {
              setOpen(false);
              setActiveIndex(-1);
              onKeyDown?.(e);
              return;
            }
            onKeyDown?.(e);
          }}
          onBlur={() => commit(draft)}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          autoComplete="off"
        />
        {hasValue && (
          <button
            type="button"
            tabIndex={-1}
            className="filter-input-clear"
            // onMouseDown + preventDefault keeps input focus (no blur commit race) and clears immediately.
            onMouseDown={(e) => { e.preventDefault(); setDraft(''); commit(''); }}
            title={t('Clear', '지우기')}
          >
            <i className="fa fa-times-circle" />
          </button>
        )}
      </div>
      {dropdownVisible && (
        <ul className="recent-search__list">
          {suggestions.map((s, idx) => (
            <li
              key={s}
              className={`recent-search__item${idx === activeIndex ? ' recent-search__item--active' : ''}`}
              onMouseEnter={() => setActiveIndex(idx)}
            >
              <button
                type="button"
                className="recent-search__pick"
                onMouseDown={(e) => { e.preventDefault(); setDraft(s); commit(s); setOpen(false); }}
              >
                <i className="fa fa-history recent-search__icon" aria-hidden />
                <span className="recent-search__text">{s}</span>
              </button>
              <button
                type="button"
                className="recent-search__remove"
                title={t('Remove from recent searches', '최근 검색어에서 삭제')}
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); removeItem(s); }}
              >
                <i className="fa fa-times" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
